/**
 * StatBlock: `stat` content block.
 *
 * data: { items: [{ value, label }] }
 *
 * A row of authored stat figures (value + label). This is the AUTHORED stat * block, distinct from the dynamic `ecosystem-stats` block, which pulls live
 * platform metrics.
 *
 * Degrades gracefully: a missing / non-array / empty `items` renders nothing;
 * entries with neither value nor label are skipped.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';

export default function StatBlock({ data }) {
    const { items } = data || {};

    if (!Array.isArray(items)) {
        return null;
    }

    // Drop fully-empty entries; keep partial ones (value-only or label-only).
    const validItems = items.filter(
        (item) =>
            item &&
            (item.value !== undefined ||
                (item.label !== undefined && item.label !== ''))
    );
    if (validItems.length === 0) {
        return null;
    }

    // Each stat is a designed callout card (background, border, rounded
    // corners, padding, soft shadow via .content-stat-card in Content.css),
    // Column count scales with the number of stats. A lone stat renders as a
    // contained, centered callout card (capped width, centered text via
    // .content-stat-card--solo) so it reads as a focal point rather than a big
    // number floating left in a full-width bar; multiple stats lay out as a
    // 2-3 up left-aligned card row.
    const count = validItems.length;
    const isSolo = count === 1;
    const colSpan = isSolo
        ? { xs: 1 }
        : count === 2
          ? { xs: 1, sm: 2 }
          : { xs: 1, sm: 2, md: 3 };
    const cardClass = isSolo
        ? 'content-stat-card content-stat-card--solo'
        : 'content-stat-card';

    return (
        <Row className="g-3 justify-content-center" {...colSpan}>
            {validItems.map((item, index) => (
                // index is a stable key for this render-only,
                // non-reorderable authored list.
                <Col key={`stat-${index}`}>
                    <div className={cardClass}>
                        {item.value !== undefined && (
                            <div className="content-stat__value fw-bold text-primary">
                                {item.value}
                            </div>
                        )}
                        {item.label && (
                            <div className="text-muted small">{item.label}</div>
                        )}
                    </div>
                </Col>
            ))}
        </Row>
    );
}

StatBlock.propTypes = {
    data: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                ]),
                label: PropTypes.string,
            })
        ),
    }),
};

StatBlock.defaultProps = {
    data: {},
};
